// @greenlight-spec: 48-user-passthrough.md#snowflake
// @greenlight-spec: decisions/0026-integration-catalog-and-connection-profiles.md
import { backgroundAccessAgentInstruction } from './background-access-hints.js';
import { TEST_SIGN_IN_STEP } from './sign-in-test-hints.js';
/**
 * Roles a Snowflake credential may never pin. Snowflake blocks these from OAuth
 * sessions by default; registration refuses them so a credential can't be made
 * that every connect would fail on.
 */
export const SNOWFLAKE_BLOCKED_ROLES = [
    'ACCOUNTADMIN',
    'SECURITYADMIN',
    'ORGADMIN',
    'GLOBALORGADMIN',
];
/**
 * The security integration IT creates (step 1), rendered by the register dialog
 * with the form's redirect URI and a name derived from the integration's slug.
 */
export const SNOWFLAKE_SECURITY_INTEGRATION_STATEMENT = {
    title: 'Security integration for this form',
    template: [
        'CREATE SECURITY INTEGRATION {security_integration}',
        '  TYPE = OAUTH',
        '  ENABLED = TRUE',
        '  OAUTH_CLIENT = CUSTOM',
        "  OAUTH_CLIENT_TYPE = 'CONFIDENTIAL'",
        "  OAUTH_REDIRECT_URI = '{redirect_uri}'",
        '  OAUTH_ENFORCE_PKCE = TRUE',
        '  OAUTH_ISSUE_REFRESH_TOKENS = TRUE',
        '  OAUTH_REFRESH_TOKEN_VALIDITY = 7776000',
        "  BLOCKED_ROLES_LIST = ('SYSADMIN', 'USERADMIN');",
    ].join('\n'),
};
export const SNOWFLAKE_USER_STEPS = [
    {
        title: 'Create the security integration',
        body: 'As ACCOUNTADMIN (or a role with CREATE INTEGRATION), run the statement shown below this guide. It is a custom confidential OAuth client (OAUTH_CLIENT = CUSTOM) whose only redirect URI is the connect callback on this form, with PKCE enforced and refresh tokens on for up to 90 days (7776000 seconds); a person reconnects when theirs expires.\n' +
            'Add every role apps must never reach to BLOCKED_ROLES_LIST. Snowflake already blocks ACCOUNTADMIN, SECURITYADMIN, ORGADMIN, and GLOBALORGADMIN, and Greenlight refuses a credential for any of them.',
    },
    {
        title: 'Read the client id and secret back',
        body: "Run SELECT SYSTEM$SHOW_OAUTH_CLIENT_SECRETS('<integration name>'); with the integration's name in upper case. Put OAUTH_CLIENT_ID in Client ID on this form and paste OAUTH_CLIENT_SECRET under Credentials. Greenlight vaults the secret and never exposes it to apps.",
    },
    {
        title: 'Name each Snowflake user by their SSO email',
        body: 'Each person needs a Snowflake user whose LOGIN_NAME is the email they sign in to Greenlight with, and the role the credential pins. When they connect, Greenlight compares the username Snowflake returns with their Greenlight email, ignoring case, and refuses any other account.\n' +
            'If your account is federated (SAML to Okta or Entra), the Snowflake login page signs them in through your identity provider; nothing else is configured for it.',
    },
    {
        title: 'Close secondary roles',
        body: "A connection consents to one role, so OAuth sessions can't activate secondary roles; set a session policy so nothing else can either:\n" +
            'CREATE SESSION POLICY greenlight_primary_role_only ALLOWED_SECONDARY_ROLES = ();\n' +
            'ALTER USER <user> SET SESSION POLICY greenlight_primary_role_only; (or set it on the account).',
    },
    {
        title: "Limit the integration to Greenlight's egress",
        body: "Create a network policy allowing only the public IP addresses your Greenlight install's traffic leaves from, then attach it to the integration:\n" +
            "ALTER SECURITY INTEGRATION <integration name> SET NETWORK_POLICY = '<policy>';\n" +
            'A leaked access or refresh token is then useless from anywhere else.',
    },
    {
        title: 'Add one credential per role',
        body: "Name the role on each credential; it is fixed once saved. An app grant binds one credential, so it runs in that credential's role and no other: Greenlight sets the SQL API's role on every statement, whatever role the app names. To offer another role, add another credential (the same client id and secret serve both).\n" +
            "Each person sees Snowflake's consent page once per credential, naming the client and the role. Access tokens last ten minutes and Greenlight refreshes them. Scheduled work with no person signed in needs a Snowflake service user, not this profile.",
    },
    TEST_SIGN_IN_STEP,
];
/** What a builder agent needs to call Snowflake as the signed-in user. */
export const SNOWFLAKE_AGENT_INSTRUCTIONS = 'Calls run as the signed-in user, in the role of the credential the grant binds. Forward the X-Greenlight-Actor-Token header from the incoming request on every call; a call without it is refused. ' +
    'Submit SQL with POST /api/v2/statements and a JSON body ({"statement": "...", "warehouse": "..."}); Greenlight sets the body\'s role to the credential\'s role on every statement, whatever role you send, so leave it out. ' +
    'A user who has not connected gets 401 proxy.user_connection_required with details.connect_url: show them a Connect button that opens it, never a redirect they did not click. ' +
    backgroundAccessAgentInstruction('a Snowflake service user');
//# sourceMappingURL=snowflake-user-guide.js.map